import { RoadmapItem, Template } from '../types';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { api, getErrorMessage, handleServiceError } from './api';

// ====================================================================
// ROADMAP SERVICE
// ====================================================================

export const roadmapService = {
  /**
   * Busca todos os items do roadmap de uma proposta
   */
  getByProposal: async (proposalId: number): Promise<RoadmapItem[]> => {
    try {
      const response = await api.get(API_ENDPOINTS.ROADMAP.LIST, {
        params: { proposal_id: proposalId },
      });
      return response?.data || [];
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'get_by_proposal' });
    }
  },

  /**
   * Cria um novo item do roadmap
   */
  create: async (proposalId: number, topic: string, instruction: string, position: number): Promise<RoadmapItem> => {
    try {
      const response = await api.post(API_ENDPOINTS.ROADMAP.LIST, {
        proposal_id: proposalId,
        topic,
        instruction,
        position,
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'create_' });
    }
  },

  /**
   * Atualiza um item do roadmap
   */
  update: async (itemId: number, data: Partial<RoadmapItem>): Promise<RoadmapItem> => {
    try {
      const response = await api.patch(API_ENDPOINTS.ROADMAP.DETAILS(itemId), data);
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'update_' });
    }
  },

  /**
   * Deleta um item do roadmap
   */
  delete: async (itemId: number): Promise<void> => {
    try {
      await api.delete(API_ENDPOINTS.ROADMAP.DETAILS(itemId));
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'delete_' });
    }
  },

  /**
   * Gera estrutura de roadmap a partir de um documento usando IA
   * Timeout aumentado para 3 minutos pois processamento de documentos pode demorar
   */
  generateFromDocument: async (documentId: number, reasoningLevel: string = 'default'): Promise<{ topics: Array<{ topic: string; instruction: string; position: number }> }> => {
    try {
      const response = await api.post(API_ENDPOINTS.ROADMAP.EXTRACT_FROM_DOCUMENT, {
        document_id: documentId,
        reasoning_level: reasoningLevel,
        temperature: 0.7,
        max_tokens: 2000,
        top_p: 0.9,
      }, {
        timeout: 180000 // 3 minutos - extração de documentos pode demorar
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'generate_from_document' });
    }
  },

  /**
   * Gera estrutura de roadmap a partir de uma instrução de texto usando IA
   */
  generateFromInstruction: async (proposalId: number, instruction: string, reasoningLevel: string = 'default', useWebSearch: boolean = false, documentId?: number | null, templateId?: number | null, templateType?: 'native' | 'organization' | null): Promise<{ topics: Array<{ topic: string; instruction: string; position: number }> }> => {
    try {
      const payload: Record<string, unknown> = {
        proposal_id: proposalId,
        instruction,
        reasoning_level: reasoningLevel,
        use_web_search: useWebSearch,
      };
      if (documentId != null) payload.document_id = documentId;
      if (templateId != null && templateType) {
        payload.template_id = templateId;
        payload.template_type = templateType;
      }

      console.log('[roadmapService] Enviando requisição generateFromInstruction:', {
        url: API_ENDPOINTS.ROADMAP.GENERATE_FROM_INSTRUCTION,
        payload
      });
      
      const response = await api.post(API_ENDPOINTS.ROADMAP.GENERATE_FROM_INSTRUCTION, payload);
      
      console.log('[roadmapService] Resposta recebida:', response?.data);
      return response?.data;
    } catch (error) {
      console.error('[roadmapService] Erro em generateFromInstruction:', error);
      handleServiceError(error, { feature: 'roadmap', action: 'generate_from_instruction' });
    }
  },

  /**
   * Gera instrução para um tópico específico usando IA
   */
  generateInstruction: async (proposalId: number, topic: string, reasoningLevel: string = 'default', useWebSearch: boolean = false): Promise<string> => {
    try {
      const response = await api.post(API_ENDPOINTS.ROADMAP.GENERATE_INSTRUCTION, {
        proposal_id: proposalId,
        topic_name: topic,
        reasoning_level: reasoningLevel,
        use_web_search: useWebSearch,
      });

      return response?.data?.instruction || '';
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'generate_instruction' });
    }
  },

  /**
   * Gera instruções para todos os tópicos sem instrução
   */
  batchGenerateInstructions: async (proposalId: number, reasoningLevel: string = 'default', llmMeta?: { provider?: string | null; model?: string | null }, useWebSearch: boolean = false): Promise<{ generated_count: number }> => {
    try {
      const body: Record<string, unknown> = {
        proposal_id: proposalId,
        reasoning_level: reasoningLevel,
        use_web_search: useWebSearch,
      };
      if (llmMeta?.provider) body.llm_provider = llmMeta.provider;
      if (llmMeta?.model) body.llm_model = llmMeta.model;

      const response = await api.post(API_ENDPOINTS.ROADMAP.BATCH_GENERATE_INSTRUCTIONS, body, {
        timeout: 600000, // 10 minutos para operações em lote
      });

      return {
        generated_count: parseInt(response?.data?.message?.match(/\d+/)?.[0] || '0')
      };
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'batch_generate_instructions' });
    }
  },

  /**
   * Verifica o status dos tópicos anteriores a um tópico específico
   */
  checkPreviousTopics: async (roadmapId: number): Promise<{
    current_topic: string;
    current_position: number;
    total_previous: number;
    filled_count: number;
    empty_count: number;
    empty_topics: Array<{ id: number; topic: string; position: number }>;
    filled_topics?: Array<{ id: number; topic: string; position: number }>;
    all_previous_filled: boolean;
  }> => {
    try {
      const response = await api.get(`/roadmap/${roadmapId}/check-previous`);
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'check_previous_topics' });
    }
  },

  /**
   * Reordena um item do roadmap
   * NOTA: Backend não tem endpoint específico para reordenação,
   * então vamos usar o update para mudar a posição
   */
  reorder: async (_proposalId: number, itemId: number, newPosition: number): Promise<void> => {
    try {
      await api.patch(API_ENDPOINTS.ROADMAP.DETAILS(itemId), {
        position: newPosition,
      });
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'reorder' });
    }
  },

  /**
   * Usa IA para sugerir a ordem ideal dos tópicos baseado no mapa conceitual
   */
  suggestOrder: async (proposalId: number, reasoningLevel: string = 'low', llmMeta?: { provider?: string | null; model?: string | null }): Promise<{
    status: string;
    suggested_order: Array<{
      topic: string;
      position: number;
      reason: string;
      roadmap_id?: number;
    }>;
    rationale: string;
    total_topics: number;
  }> => {
    try {
      const body: Record<string, unknown> = {
        proposal_id: proposalId,
        reasoning_level: reasoningLevel,
      };
      if (llmMeta?.provider) body.llm_provider = llmMeta.provider;
      if (llmMeta?.model) body.llm_model = llmMeta.model;

      const response = await api.post('/roadmap/suggest-order', body, {
        timeout: 180000, // 3 minutos para operações longas de IA
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'suggest_order' });
    }
  },
};

// ====================================================================
// TEMPLATE SERVICE
// ====================================================================

export const templateService = {
  /**
   * Lista todos os templates (modelos de roteiro) do usuário
   */
  list: async (proposalType?: string): Promise<Template[]> => {
    try {
      const response = await api.get(API_ENDPOINTS.TEMPLATES.LIST, { params: { proposal_type: proposalType || undefined } });

      // Mapeia para o formato esperado pelo frontend
      return (response?.data || []).map((template: any) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        user_id: template.user_id,
        organization_id: template.organization_id ?? null,
        item_count: template.items?.length || 0,
        items: template.items?.map((item: any) => ({
          topic: item.topic,
          instruction: item.instruction || '',
          position: item.position,
          character_limit: item.character_limit ?? null,
          word_limit: item.word_limit ?? null,
          limit_type: item.limit_type ?? null,
          is_required: item.is_required ?? false,
        })) || [],
        created_at: template.created_at,
      }));
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'list' });
    }
  },

  /**
   * Cria um template a partir de uma proposta existente
   */
  createFromProposal: async (proposalId: number, name: string, description?: string, specialistTypes: string[] = []): Promise<Template> => {
    try {
      const response = await api.post(API_ENDPOINTS.TEMPLATES.CREATE_FROM_PROPOSAL(proposalId), {
        name,
        description: description || '',
        specialist_types: specialistTypes,
      });

      const template = response?.data;
      return {
        id: template.id,
        name: template.name,
        description: template.description,
        user_id: template.user_id,
        item_count: template.items?.length || 0,
        items: template.items?.map((item: any) => ({
          topic: item.topic,
          instruction: item.instruction || '',
          position: item.position,
        })) || [],
        created_at: template.created_at,
      };
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'create_from_proposal' });
    }
  },

  /**
   * Aplica um template em uma proposta (substitui o roteiro atual)
   */
  applyToProposal: async (templateId: number, proposalId: number): Promise<void> => {
    try {
      await api.post(API_ENDPOINTS.TEMPLATES.APPLY_TO_PROPOSAL(templateId, proposalId));
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'apply_to_proposal' });
    }
  },

  /**
   * Deleta um template
   */
  delete: async (templateId: number): Promise<void> => {
    try {
      await api.delete(API_ENDPOINTS.TEMPLATES.DELETE(templateId));
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'delete_' });
    }
  },

  /**
   * Atualiza um template existente (nome, descrição e itens completos)
   */
  update: async (
    templateId: number,
    name: string,
    items: Array<{
      topic: string;
      instruction: string;
      position: number;
      character_limit?: number | null;
      word_limit?: number | null;
      limit_type?: string | null;
      is_required?: boolean;
    }>,
    description?: string
  ): Promise<Template> => {
    try {
      const response = await api.put(API_ENDPOINTS.TEMPLATES.UPDATE(templateId), {
        name,
        description: description || '',
        items: items.map((item, idx) => ({
          topic: item.topic,
          instruction: item.instruction,
          position: item.position ?? idx + 1,
          is_required: item.is_required ?? false,
          character_limit: item.character_limit ?? null,
          word_limit: item.word_limit ?? null,
          limit_type: item.limit_type ?? null,
        })),
      });
      const template = response?.data;
      return {
        id: template.id,
        name: template.name,
        description: template.description,
        user_id: template.user_id,
        item_count: template.items?.length || 0,
        items: (template.items || []).map((item: any) => ({
          topic: item.topic,
          instruction: item.instruction || '',
          position: item.position,
          character_limit: item.character_limit ?? null,
          word_limit: item.word_limit ?? null,
          limit_type: item.limit_type ?? null,
          is_required: item.is_required ?? false,
        })),
        created_at: template.created_at,
      };
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'update_template' });
      throw error;
    }
  },

  /**
   * Sobrescreve um template existente: apaga e cria novo com o mesmo nome a partir da proposta
   */
  overwriteFromProposal: async (templateId: number, templateName: string, proposalId: number, description?: string, specialistTypes: string[] = []): Promise<Template> => {
    try {
      await api.delete(API_ENDPOINTS.TEMPLATES.DELETE(templateId));
      const response = await api.post(API_ENDPOINTS.TEMPLATES.CREATE_FROM_PROPOSAL(proposalId), {
        name: templateName,
        description: description || '',
        specialist_types: specialistTypes,
      });
      const template = response?.data;
      return {
        id: template.id,
        name: template.name,
        description: template.description,
        user_id: template.user_id,
        item_count: template.items?.length || 0,
        items: (template.items || []).map((item: any) => ({
          topic: item.topic,
          instruction: item.instruction || '',
          position: item.position,
        })),
        created_at: template.created_at,
      };
    } catch (error) {
      handleServiceError(error, { feature: 'roadmap', action: 'overwrite_template' });
      throw error;
    }
  },
};
